import { useState } from 'react';
import axios from 'axios';
import { ENV } from '../utils/env.js';
import { getIntegrationEndpoint, getAutoFixPollingConfig, getBulkActionConfig } from '../config/integrationConfig';
import { useAutoFixPolling } from './useAutoFixPolling';
import { showFreeAssessmentToast } from '../utils/freeAssessmentRedirect';

// Central helper: extract backend error message consistently
const extractBackendMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  try {
    return (
      error?.response?.data?.error ||
      error?.response?.data?.message ||
      error?.message ||
      fallback
    );
  } catch (_) {
    return fallback;
  }
}

/**
 * Hook for managing integration actions (individual and bulk)
 * @param {string} integrationType - The type of integration (s3, repositories, etc.)
 * @param {string} integrationId - The ID of the specific integration instance
 * @param {object} integrationConfig - The configuration object for this integration type
 * @param {function} showToast - Toast notification function
 * @param {function} loadFindings - Function to reload findings after actions
 * @param {function} getFindingId - Function to extract finding ID from finding object
 * @param {function} setSelected - Function to update selection state
 * @param {function} setRowErrors - Function to update row errors state
 * @param {object} config - Application configuration for validation
 */
export const useIntegrationActions = (
  integrationType, 
  integrationId, 
  integrationConfig, 
  showToast, 
  loadFindings, 
  getFindingId,
  setSelected,
  setRowErrors,
  config = {},
  updateFindingStatus = null
) => {
  const isRedirectIntegration = integrationType === 'github' || integrationType === 's3';
  const redirectToFreeAssessment = () => {
    showFreeAssessmentToast();
  };
  // Action loading states
  const [actionLoading, setActionLoading] = useState({});
  const [bulkActionLoading, setBulkActionLoading] = useState({});

  // Get auto-fix polling configuration
  const autoFixConfig = getAutoFixPollingConfig(integrationType);
  
  // Create a function to handle finding status updates
  const handleFindingStatusUpdate = async (findingId, statusData) => {
    // Use the provided updateFindingStatus function if available, otherwise fall back to loadFindings
    if (updateFindingStatus) {
      updateFindingStatus(findingId, statusData);
    } else {
      await loadFindings();
    }
  };

  // Auto-fix polling hook
  const autoFixPolling = useAutoFixPolling(
    showToast,
    handleFindingStatusUpdate,
    {
      pollInterval: autoFixConfig.pollInterval,
      maxPollTime: autoFixConfig.maxPollTime,
      enableLogging: autoFixConfig.enableLogging
    }
  );

  // Validation functions
  const validateTicketingCredentials = () => {
    const hasJiraUrl = config.jira_url && config.jira_url.trim() !== '';
    const hasFreshserviceUrl = config.freshservice_url && config.freshservice_url.trim() !== '';
    
    if (!hasJiraUrl && !hasFreshserviceUrl) {
      showToast("Please enter the ticketing credentials in the configuration page", "error");
      return false;
    }
    return true;
  };

  const validateVaultCredentials = () => {
    const hasVaultUrl = config.hashicorp_vault_url && config.hashicorp_vault_url.trim() !== '';
    
    if (!hasVaultUrl) {
      showToast("Please enter the vault credentials in the configuration page", "error");
      return false;
    }
    return true;
  };

  // Individual action handlers
  const handleCreateJiraTicket = async (finding, index) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateTicketingCredentials()) return;
    
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Try to use integration-specific bulk endpoint first, fall back to generic
      const endpoint = getIntegrationEndpoint(integrationType, 'createTicket', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/create` ||
                     `api/jira/tickets/bulk/create`;

      const response = await axios.post(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: [findingId] },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast('JIRA ticket created successfully!', 'success');
        await loadFindings();
      }
      
      // Handle failed items
      if (response.data.failed_count > 0 && response.data.failed.length > 0) {
        const error = response.data.failed[0].error;
        showToast(error, 'error');
      }
    } catch (error) {
      console.error('Error creating JIRA ticket:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to create ticket. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const handleIgnoreFindings = async (finding, index) => {
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Try to use integration-specific bulk endpoint first, fall back to generic
      const endpoint = getIntegrationEndpoint(integrationType, 'ignoreFindings', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/ignore` ||
                     `api/findings/bulk/ignore`;

      const response = await axios.put(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: [findingId] },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast(`${integrationConfig?.displayName || 'Integration'} findings ignored successfully!`, 'success');
        await loadFindings();
      }
      
      // Handle failed items
      if (response.data.failed_count > 0 && response.data.failed.length > 0) {
        const error = response.data.failed[0].error;
        showToast(error, 'error');
      }
    } catch (error) {
      console.error('Error ignoring findings:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to ignore findings. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const handleResolveTicket = async (finding, index) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateTicketingCredentials()) return;
    
    try {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      // Try to use integration-specific bulk endpoint first, fall back to generic
      const endpoint = getIntegrationEndpoint(integrationType, 'resolveTicket', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/resolve` ||
                     `api/jira/tickets/bulk/resolve`;

      const response = await axios.put(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: [findingId] },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast('Vulnerability resolved successfully!', 'success');
        await loadFindings();
      }
      
      // Handle failed items
      if (response.data.failed_count > 0 && response.data.failed.length > 0) {
        const error = response.data.failed[0].error;
        showToast(error, 'error');
      }
    } catch (error) {
      console.error('Error resolving ticket:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to resolve ticket. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      const findingId = getFindingId(finding, index);
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  const handleMoveToVault = async (finding, index) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateVaultCredentials()) return;
    
    const findingId = getFindingId(finding, index);
    try {
      setActionLoading((prev) => ({ ...prev, [findingId]: true }));

      const orgId = localStorage.getItem('orgId') || '';
      const baseUrl = ENV.REACT_APP_AI_URL || ENV.AI_URL;
      
      // Try integration-specific endpoint first, fall back to generic
      const endpoint = getIntegrationEndpoint(integrationType, 'moveToVault', { id: integrationId }) ||
                     `api/v1/${integrationType}/remediation` ||
                     `api/v1/remediation`;

      const response = await axios.post(
        `${baseUrl}${endpoint}`,
        { finding_id: findingId },
        {
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
            'X-Org-Id': orgId,
          },
        }
      );

      showToast('Move to Vault successful!', 'success');
      await loadFindings();
    } catch (error) {
      console.error('Error moving to vault:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to move to Vault. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      setActionLoading((prev) => ({ ...prev, [findingId]: false }));
    }
  };

  // Generic action handler that can be used by integration configs
  const handleGenericAction = async (actionConfig, finding, index) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    const findingId = getFindingId(finding, index);
    
    if (actionConfig.action === 'api' && actionConfig.endpoint) {
      try {
        setActionLoading(prev => ({ ...prev, [findingId]: true }));
        
        const endpoint = actionConfig.endpoint
          .replace('{id}', integrationId)
          .replace('{finding_id}', findingId);
        
        // Determine base URL based on configuration
        let baseUrl;
        switch (actionConfig.baseUrl) {
          case 'REACT_APP_URL':
            baseUrl = ENV.REACT_APP_URL;
            break;
          case 'AI_URL':
            baseUrl = ENV.AI_URL || ENV.REACT_APP_AI_URL;
            break;
          case 'API_URL':
          default:
            baseUrl = ENV.API_URL;
            break;
        }
        
        // Build request payload
        let requestData = { finding_ids: [findingId] };
        if (actionConfig.payload) {
          if (actionConfig.key === 'move_to_vault') {
            // Special handling for vault actions
            requestData = {
              items: [
                {
                  ...actionConfig.payload,
                  finding_ids: [findingId]
                }
              ]
            };
          } else {
            // For status updates and other actions
            requestData = {
              ...requestData,
              ...actionConfig.payload
            };
          }
        }
        
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('jwt')}`
        };
        
        // Add org header for AI URL requests
        if (actionConfig.baseUrl === 'AI_URL') {
          headers['X-Org-Id'] = localStorage.getItem('orgId') || '';
        }
        
        const response = await axios({
          method: actionConfig.method || 'POST',
          url: `${baseUrl}${endpoint}`,
          headers,
          data: requestData
        });
        
        // Handle response based on action type
        if (actionConfig.key === 'auto_fix' && autoFixConfig.enabled) {
          // Special handling for auto-fix actions
          const jobId = response.data?.job_id || response.data?.id;
          
          if (jobId) {
            // Immediately update finding status to "processing"
            await handleFindingStatusUpdate(findingId, { status: 'processing' });
            
            // Start polling for auto-fix status
            showToast('Auto-fix initiated! Monitoring progress...', 'success');
            autoFixPolling.startPolling(jobId, findingId, integrationType);
          } else {
            // No job ID returned, treat as regular action
            showToast(`${actionConfig.label} successful!`, 'success');
            await loadFindings();
          }
        } else if (response.data?.success_count !== undefined) {
          // Bulk API response format
          const { success_count, failed_count } = response.data;
          if (success_count > 0) {
            showToast(`${actionConfig.label} successful!`, 'success');
          }
          if (failed_count > 0 && response.data.failed?.length > 0) {
            const error = response.data.failed[0].error;
            showToast(error, 'error');
          }
          await loadFindings();
        } else {
          // Simple success response
          showToast(`${actionConfig.label} successful!`, 'success');
          await loadFindings();
        }
      } catch (error) {
        console.error(`Error executing ${actionConfig.label}:`, error);
        const backendMessage = extractBackendMessage(error, `Failed to ${actionConfig.label.toLowerCase()}`);
        showToast(backendMessage, 'error');
      } finally {
        setActionLoading(prev => ({ ...prev, [findingId]: false }));
      }
    }
  };

  // Bulk action handlers
  const handleBulkCreateJiraTickets = async (selectedFindings) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateTicketingCredentials()) return;
    
    try {
      setBulkActionLoading(prev => ({ ...prev, createTickets: true }));
      
      const findingIds = selectedFindings.map(f => f.id || String(f.scan_id || ''));
      
      if (findingIds.length === 0) {
        showToast('No findings selected', 'error');
        return;
      }

      const endpoint = getIntegrationEndpoint(integrationType, 'createTicket', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/create` ||
                     `api/jira/tickets/bulk/create`;

      const response = await axios.post(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: findingIds },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast(`Successfully created ${success_count} JIRA ticket${success_count > 1 ? 's' : ''}`, 'success');
      }
      
      // Show individual error messages for failed items as toasts
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const errorMessage = failedItem.error || "Failed to create ticket";
          showToast(`Finding ${failedItem.finding_id}: ${errorMessage}`, "error");
        });
      }

      // Build error map from failed items
      const errors = {};
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const rowId = failedItem.finding_id;
          errors[rowId] = true;
        });
      }

      setRowErrors(errors);

      // Deselect successfully processed items only
      if (success_count > 0) {
        const successIds = response.data.success ? response.data.success.map(s => s.finding_id) : [];
        setSelected(prev => {
          const next = { ...prev };
          successIds.forEach(id => { delete next[id]; });
          return next;
        });
      }

      await loadFindings();
      
    } catch (error) {
      console.error('Error creating bulk JIRA tickets:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to create tickets. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      setBulkActionLoading(prev => ({ ...prev, createTickets: false }));
    }
  };

  const handleBulkIgnoreFindings = async (selectedFindings) => {
    try {
      setBulkActionLoading(prev => ({ ...prev, ignoreFindings: true }));
      
      const findingIds = selectedFindings.map(f => f.id || String(f.scan_id || ''));
      
      if (findingIds.length === 0) {
        showToast('No findings selected', 'error');
        return;
      }

      const endpoint = getIntegrationEndpoint(integrationType, 'ignoreFindings', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/ignore` ||
                     `api/findings/bulk/ignore`;

      const response = await axios.put(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: findingIds },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast(`Successfully ignored ${success_count} finding${success_count > 1 ? 's' : ''}`, 'success');
      }
      
      // Show individual error messages for failed items as toasts
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const errorMessage = failedItem.error || "Failed to ignore finding";
          showToast(`Finding ${failedItem.finding_id}: ${errorMessage}`, "error");
        });
      }

      // Build error map from failed items
      const errors = {};
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const rowId = failedItem.finding_id;
          errors[rowId] = true;
        });
      }

      setRowErrors(errors);

      // Deselect successfully processed items only
      if (success_count > 0) {
        const successIds = response.data.success ? response.data.success.map(s => s.finding_id) : [];
        setSelected(prev => {
          const next = { ...prev };
          successIds.forEach(id => { delete next[id]; });
          return next;
        });
      }

      await loadFindings();
      
    } catch (error) {
      console.error('Error ignoring bulk findings:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to ignore findings. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      setBulkActionLoading(prev => ({ ...prev, ignoreFindings: false }));
    }
  };

  const handleBulkResolveTickets = async (selectedFindings) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateTicketingCredentials()) return;
    
    try {
      setBulkActionLoading(prev => ({ ...prev, resolveTickets: true }));
      
      const findingIds = selectedFindings.map(f => f.id || String(f.scan_id || ''));
      
      if (findingIds.length === 0) {
        showToast('No findings selected', 'error');
        return;
      }

      const endpoint = getIntegrationEndpoint(integrationType, 'resolveTicket', { id: integrationId }) ||
                     `api/${integrationType}-jira/tickets/bulk/resolve` ||
                     `api/jira/tickets/bulk/resolve`;

      const response = await axios.put(
        `${ENV.API_URL}${endpoint}`,
        { finding_ids: findingIds },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem('jwt')}`
          }
        }
      );

      const { success_count } = response.data;
      
      if (success_count > 0) {
        showToast(`Successfully resolved ${success_count} ticket${success_count > 1 ? 's' : ''}`, 'success');
      }
      
      // Show individual error messages for failed items as toasts
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const errorMessage = failedItem.error || "Failed to resolve ticket";
          showToast(`Finding ${failedItem.finding_id}: ${errorMessage}`, "error");
        });
      }

      // Build error map from failed items
      const errors = {};
      if (response.data.failed && response.data.failed.length > 0) {
        response.data.failed.forEach(failedItem => {
          const rowId = failedItem.finding_id;
          errors[rowId] = true;
        });
      }

      setRowErrors(errors);

      // Deselect successfully processed items only
      if (success_count > 0) {
        const successIds = response.data.success ? response.data.success.map(s => s.finding_id) : [];
        setSelected(prev => {
          const next = { ...prev };
          successIds.forEach(id => { delete next[id]; });
          return next;
        });
      }

      await loadFindings();
      
    } catch (error) {
      console.error('Error resolving bulk tickets:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to resolve tickets. Please try again.');
      showToast(backendMessage, 'error');
    } finally {
      setBulkActionLoading(prev => ({ ...prev, resolveTickets: false }));
    }
  };

  const handleBulkMoveToVault = async (selectedFindings) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!validateVaultCredentials()) return;
    
    try {
      setBulkActionLoading(prev => ({ ...prev, moveToVault: true }));
      
      const findingIds = selectedFindings.map(f => f.id || String(f.scan_id || ''));
      
      if (findingIds.length === 0) {
        showToast('No findings selected', 'error');
        return;
      }

      const orgId = localStorage.getItem('orgId') || '';
      const baseUrl = ENV.REACT_APP_AI_URL || ENV.AI_URL;
      
      // Process each finding individually since the API might not support bulk
      const results = await Promise.allSettled(
        findingIds.map(findingId => {
          const endpoint = getIntegrationEndpoint(integrationType, 'moveToVault', { id: integrationId }) ||
                         `api/v1/${integrationType}/remediation` ||
                         `api/v1/remediation`;
          
          return axios.post(
            `${baseUrl}${endpoint}`,
            { finding_id: findingId },
            {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('jwt')}`,
                'X-Org-Id': orgId,
              },
            }
          );
        })
      );

      let successCount = 0;
      const errors = {};
      
      results.forEach((result, index) => {
        const findingId = findingIds[index];
        if (result.status === 'fulfilled') {
          successCount++;
        } else {
          errors[findingId] = true;
          const errorMessage = extractBackendMessage(result.reason, 'Failed to move to vault');
          showToast(`Finding ${findingId}: ${errorMessage}`, "error");
        }
      });

      setRowErrors(errors);

      // Deselect successfully processed items only
      if (successCount > 0) {
        const successIds = results
          .map((result, index) => result.status === 'fulfilled' ? findingIds[index] : null)
          .filter(Boolean);
        
        setSelected(prev => {
          const next = { ...prev };
          successIds.forEach(id => { delete next[id]; });
          return next;
        });
      }

      // Show summary toast
      if (successCount > 0) {
        showToast(`Successfully moved ${successCount} finding(s) to vault!`, "success");
      }

      await loadFindings();
      
    } catch (error) {
      console.error('Error in bulk move to vault:', error);
      const backendMessage = extractBackendMessage(error, 'Failed to move findings to vault');
      showToast(backendMessage, 'error');
    } finally {
      setBulkActionLoading(prev => ({ ...prev, moveToVault: false }));
    }
  };

  // Generic config-driven bulk action handler
  const handleGenericBulkAction = async (actionConfig, selectedFindings) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    if (!actionConfig || !actionConfig.endpoint) {
      showToast(`Action configuration missing for ${actionConfig?.key || 'unknown action'}`, 'error');
      return;
    }

    // Check validation requirements
    if (actionConfig.requiresValidation === 'ticketing' && !validateTicketingCredentials()) {
      return;
    }
    if (actionConfig.requiresValidation === 'vault' && !validateVaultCredentials()) {
      return;
    }

    try {
      setBulkActionLoading(prev => ({ ...prev, [actionConfig.key]: true }));
      
      const findingIds = selectedFindings.map(f => f.id || String(f.scan_id || ''));
      
      if (findingIds.length === 0) {
        showToast('No findings selected', 'error');
        return;
      }

      // Determine base URL based on configuration
      let baseUrl;
      switch (actionConfig.baseUrl) {
        case 'REACT_APP_URL':
          baseUrl = ENV.REACT_APP_URL;
          break;
        case 'AI_URL':
          baseUrl = ENV.AI_URL || ENV.REACT_APP_AI_URL;
          break;
        case 'API_URL':
        default:
          baseUrl = ENV.API_URL;
          break;
      }

      // Build request payload
      let requestData = { finding_ids: findingIds };
      
      if (actionConfig.payload) {
        if (actionConfig.payloadStructure === "vault") {
          // Special vault structure: {items: [{scan_type, finding_ids}]}
          requestData = {
            items: [
              {
                ...actionConfig.payload,
                finding_ids: findingIds
              }
            ]
          };
        } else {
          // Regular payload merge
          requestData = {
            ...requestData,
            ...actionConfig.payload
          };
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('jwt')}`
      };

      // Add org header for AI URL requests
      if (actionConfig.baseUrl === 'AI_URL') {
        headers['X-Org-Id'] = localStorage.getItem('orgId') || '';
      }

      const response = await axios({
        method: actionConfig.method || 'POST',
        url: `${baseUrl}${actionConfig.endpoint}`,
        headers,
        data: requestData
      });

      // Process response
      if (response.data?.success_count !== undefined) {
        // Bulk API response format
        const { success_count, failed_count } = response.data;
        
        if (success_count > 0) {
          showToast(`Successfully processed ${success_count} finding${success_count > 1 ? 's' : ''}`, 'success');
        }
        
        // Show individual error messages for failed items as toasts
        if (response.data.failed && response.data.failed.length > 0) {
          response.data.failed.forEach(failedItem => {
            const errorMessage = failedItem.error || `Failed to ${actionConfig.label?.toLowerCase() || 'process'}`;
            showToast(`Finding ${failedItem.finding_id}: ${errorMessage}`, "error");
          });
        }

        // Build error map from failed items
        const errors = {};
        if (response.data.failed && response.data.failed.length > 0) {
          response.data.failed.forEach(failedItem => {
            const rowId = failedItem.finding_id;
            errors[rowId] = true;
          });
        }

        setRowErrors(errors);

        // Deselect successfully processed items only
        if (success_count > 0) {
          const successIds = response.data.success ? response.data.success.map(s => s.finding_id) : [];
          setSelected(prev => {
            const next = { ...prev };
            successIds.forEach(id => { delete next[id]; });
            return next;
          });
        }
      } else {
        // Simple success response
        showToast(`${actionConfig.label} successful!`, 'success');
        
        // Deselect all items on simple success
        setSelected({});
      }

      await loadFindings();
      
    } catch (error) {
      console.error(`Error executing bulk ${actionConfig.label}:`, error);
      const backendMessage = extractBackendMessage(error, `Failed to ${actionConfig.label?.toLowerCase() || 'process'}. Please try again.`);
      showToast(backendMessage, 'error');
    } finally {
      setBulkActionLoading(prev => ({ ...prev, [actionConfig.key]: false }));
    }
  };

  // Updated bulk action handler that supports both legacy and config-driven actions
  const handleBulkAction = async (actionKeyOrConfig, selectedFindings) => {
    if (isRedirectIntegration) {
      redirectToFreeAssessment();
      return;
    }
    // If it's a string (legacy), convert to action config lookup
    if (typeof actionKeyOrConfig === 'string') {
      const actionConfig = getBulkActionConfig(integrationType, actionKeyOrConfig);
      if (actionConfig) {
        return handleGenericBulkAction(actionConfig, selectedFindings);
      }
      
      // Fall back to legacy handlers for backward compatibility
      switch (actionKeyOrConfig) {
        case 'createTickets':
          return handleBulkCreateJiraTickets(selectedFindings);
        case 'ignoreFindings':
          return handleBulkIgnoreFindings(selectedFindings);
        case 'resolveTickets':
          return handleBulkResolveTickets(selectedFindings);
        case 'moveToVault':
          return handleBulkMoveToVault(selectedFindings);
        default:
          showToast(`Bulk ${actionKeyOrConfig} not yet implemented for this integration type`, 'info');
      }
    } else if (typeof actionKeyOrConfig === 'object' && actionKeyOrConfig.endpoint) {
      // If it's an action config object, use generic handler directly
      return handleGenericBulkAction(actionKeyOrConfig, selectedFindings);
    } else {
      showToast('Invalid action configuration', 'error');
    }
  };

  return {
    // Loading states
    actionLoading,
    bulkActionLoading,
    
    // Individual actions
    handleCreateJiraTicket,
    handleIgnoreFindings,
    handleResolveTicket,
    handleMoveToVault,
    handleGenericAction,
    
    // Bulk actions
    handleBulkCreateJiraTickets,
    handleBulkIgnoreFindings,
    handleBulkResolveTickets,
    handleBulkMoveToVault,
    handleBulkAction,
    
    // Validation
    validateTicketingCredentials,
    validateVaultCredentials,
    
    // Auto-fix polling
    autoFixPolling
  };
};